import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';
import type { Language } from '../types';

interface MomentAdvice {
  oracleInsight: string;
  shortTermAction: string;
  shortTermTimeframe: string;
  shortTermCompleted?: boolean;
  mediumTermGoal: string;
  mediumTermTimeframe: string;
  mediumTermCompleted?: boolean;
  longTermVision: string;
  longTermTimeframe: string;
  longTermCompleted?: boolean;
  emotionGradient: string;
  inspirationalQuote?: string;
  connectedValues?: string[];
  connectedGoals?: string[];
}

interface AdviceStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  advice: MomentAdvice | null;
  onCompleteAction?: (actionType: 'short' | 'medium' | 'long', completed: boolean) => void;
  language: Language;
  entryContent?: string;
}

const TRANSLATIONS = {
  Italian: {
    insight: 'INSIGHT',
    insightSubtitle: 'Cosa ho notato',
    today: 'OGGI',
    todaySubtitle: 'Il prossimo passo',
    week: 'QUESTA SETTIMANA',
    weekSubtitle: 'L\'obiettivo',
    vision: 'VISIONE',
    visionSubtitle: 'Il tuo percorso',
    markDone: 'Fatto',
    done: 'Completato',
    swipeHint: 'Scorri per continuare',
    tapToClose: 'Tocca per chiudere',
  },
  English: {
    insight: 'INSIGHT',
    insightSubtitle: 'What I noticed',
    today: 'TODAY',
    todaySubtitle: 'The next step',
    week: 'THIS WEEK',
    weekSubtitle: 'The goal',
    vision: 'VISION',
    visionSubtitle: 'Your path',
    markDone: 'Mark Done',
    done: 'Completed',
    swipeHint: 'Swipe to continue',
    tapToClose: 'Tap to close',
  },
  Spanish: {
    insight: 'INSIGHT',
    insightSubtitle: 'Lo que noté',
    today: 'HOY',
    todaySubtitle: 'El próximo paso',
    week: 'ESTA SEMANA',
    weekSubtitle: 'El objetivo',
    vision: 'VISIÓN',
    visionSubtitle: 'Tu camino',
    markDone: 'Hecho',
    done: 'Completado',
    swipeHint: 'Desliza para continuar',
    tapToClose: 'Toca para cerrar',
  },
  German: {
    insight: 'ERKENNTNIS',
    insightSubtitle: 'Was mir aufgefallen ist',
    today: 'HEUTE',
    todaySubtitle: 'Der nächste Schritt',
    week: 'DIESE WOCHE',
    weekSubtitle: 'Das Ziel',
    vision: 'VISION',
    visionSubtitle: 'Dein Weg',
    markDone: 'Erledigt',
    done: 'Abgeschlossen',
    swipeHint: 'Wische zum Fortfahren',
    tapToClose: 'Tippen zum Schließen',
  },
  French: {
    insight: 'INSIGHT',
    insightSubtitle: 'Ce que j\'ai remarqué',
    today: "AUJOURD'HUI",
    todaySubtitle: 'La prochaine étape',
    week: 'CETTE SEMAINE',
    weekSubtitle: "L'objectif",
    vision: 'VISION',
    visionSubtitle: 'Ton chemin',
    markDone: 'Terminé',
    done: 'Complété',
    swipeHint: 'Glisse pour continuer',
    tapToClose: 'Appuie pour fermer',
  },
};

// Action slide styles - Apple-quality gradients with better contrast
const SLIDE_STYLES = [
  {
    bg: 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600',
    accent: 'text-emerald-100',
    emoji: '🎯',
    pattern: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.12) 0%, transparent 45%)',
  },
  {
    bg: 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500',
    accent: 'text-amber-100',
    emoji: '🚀',
    pattern: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 40%)',
  },
  {
    bg: 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600',
    accent: 'text-violet-200',
    emoji: '🌟',
    pattern: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)',
  },
];

const AdviceStoryModal: React.FC<AdviceStoryModalProps> = ({
  isOpen,
  onClose,
  advice,
  onCompleteAction,
  language,
  entryContent,
}) => {
  const t = TRANSLATIONS[language] || TRANSLATIONS.English;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // Calculate total slides based on available content
  const slides = React.useMemo(() => {
    if (!advice) return [];
    const slideList: Array<{
      type: 'insight' | 'short' | 'medium' | 'long';
      content: string;
      title: string;
      subtitle: string;
      completed?: boolean;
      timeframe?: string;
    }> = [];

    if (advice.shortTermAction) {
      slideList.push({
        type: 'short',
        content: advice.shortTermAction,
        title: t.today,
        subtitle: t.todaySubtitle,
        completed: advice.shortTermCompleted,
        timeframe: advice.shortTermTimeframe,
      });
    }

    if (advice.mediumTermGoal) {
      slideList.push({
        type: 'medium',
        content: advice.mediumTermGoal,
        title: t.week,
        subtitle: t.weekSubtitle,
        completed: advice.mediumTermCompleted,
        timeframe: advice.mediumTermTimeframe,
      });
    }

    if (advice.longTermVision) {
      slideList.push({
        type: 'long',
        content: advice.longTermVision,
        title: t.vision,
        subtitle: t.visionSubtitle,
        completed: advice.longTermCompleted,
        timeframe: advice.longTermTimeframe,
      });
    }

    return slideList;
  }, [advice, t]);

  const totalSlides = slides.length;

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentSlide(0);
      setProgress(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Auto-progress timer
  useEffect(() => {
    if (!isOpen || isPaused) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          // Auto-advance to next slide
          if (currentSlide < totalSlides - 1) {
            setCurrentSlide((s) => s + 1);
            return 0;
          } else {
            onClose();
            return 100;
          }
        }
        return prev + 0.5; // Slower progress for reading
      });
    }, 50);

    return () => clearInterval(interval);
  }, [isOpen, isPaused, currentSlide, totalSlides, onClose]);

  // Touch handlers for swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsPaused(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      setIsPaused(false);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
      setProgress(0);
    } else if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
      setProgress(0);
    }

    setTouchStart(null);
    setTouchEnd(null);
    setIsPaused(false);
  }, [touchStart, touchEnd, currentSlide, totalSlides]);

  const handlePrevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
      setProgress(0);
    }
  }, [currentSlide]);

  const handleNextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentSlide, totalSlides, onClose]);

  const handleCompleteAction = useCallback(
    (type: 'short' | 'medium' | 'long') => {
      const slide = slides.find((s) => s.type === type);
      if (slide && onCompleteAction) {
        onCompleteAction(type, !slide.completed);
      }
    },
    [slides, onCompleteAction]
  );

  if (!isOpen || !advice || slides.length === 0) return null;

  const currentSlideData = slides[currentSlide];
  const style = SLIDE_STYLES[currentSlide % SLIDE_STYLES.length];

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* SOLID opaque gradient background - NO transparency */}
      <div className={`absolute inset-0 ${style.bg}`} />
      
      {/* Subtle pattern overlay for texture */}
      <div className="absolute inset-0" style={{ backgroundImage: style.pattern }} />

      {/* Progress bars - Instagram style */}
      <div className="absolute top-0 left-0 right-0 z-30 flex gap-1.5 p-3 pt-safe">
        {slides.map((_, i) => (
          <div key={i} className="flex-1 h-1 bg-black/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100 ease-linear shadow-sm"
              style={{
                width: i < currentSlide ? '100%' : i === currentSlide ? `${progress}%` : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header - Compact */}
      <div className="absolute top-10 left-0 right-0 z-30 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{style.emoji}</span>
          <div>
            <p className="text-white font-black text-sm tracking-widest"
               style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
              {currentSlideData.title}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-full bg-black/30 flex items-center justify-center active:scale-95"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* SIDE Navigation Arrows - Instagram Stories Style */}
      <button
        onClick={handlePrevSlide}
        disabled={currentSlide === 0}
        className={`absolute left-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full flex items-center justify-center transition-all active:scale-90 ${
          currentSlide === 0 ? 'opacity-0 pointer-events-none' : 'bg-black/40 hover:bg-black/50'
        }`}
      >
        <ChevronLeft size={24} className="text-white" />
      </button>
      
      <button
        onClick={handleNextSlide}
        className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-black/40 hover:bg-black/50 flex items-center justify-center transition-all active:scale-90"
      >
        <ChevronRight size={24} className="text-white" />
      </button>

      {/* Navigation touch areas - invisible */}
      <div
        className="absolute left-0 top-20 bottom-40 w-1/4 z-20"
        onClick={handlePrevSlide}
      />
      <div
        className="absolute right-0 top-20 bottom-40 w-1/4 z-20"
        onClick={handleNextSlide}
      />

      {/* Main Content - Centered */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-14 py-20 z-10">
        <div
          key={currentSlide}
          className="max-w-md w-full animate-in fade-in zoom-in-95 duration-300"
        >
          {/* Content card - Clean white glass */}
          <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-6 shadow-2xl">
            {/* Main text */}
            <p className="text-gray-800 text-[17px] leading-relaxed font-medium">
              {currentSlideData.content}
            </p>

            {/* Timeframe badge */}
            {currentSlideData.timeframe && (
              <div className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider ${
                currentSlide === 0 ? 'bg-amber-100 text-amber-700' :
                currentSlide === 1 ? 'bg-blue-100 text-blue-700' :
                'bg-violet-100 text-violet-700'
              }`}>
                {currentSlideData.timeframe}
              </div>
            )}
          </div>

          {/* PROMINENT Action Button - Below card */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => handleCompleteAction(currentSlideData.type as 'short' | 'medium' | 'long')}
              className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all active:scale-95 shadow-2xl ${
                currentSlideData.completed
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white text-gray-800 hover:bg-gray-50'
              }`}
            >
              {currentSlideData.completed ? (
                <>
                  <Check size={22} strokeWidth={3} />
                  <span>{t.done}</span>
                </>
              ) : (
                <>
                  <Sparkles size={22} strokeWidth={2.5} className={
                    currentSlide === 0 ? 'text-amber-500' :
                    currentSlide === 1 ? 'text-blue-500' :
                    'text-violet-500'
                  } />
                  <span>{t.markDone}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom - Minimal dots only */}
      <div className="absolute bottom-0 left-0 right-0 pb-safe z-20">
        <div className="flex items-center justify-center gap-2 py-6">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setCurrentSlide(i);
                setProgress(0);
              }}
              className={`transition-all duration-300 rounded-full ${
                i === currentSlide
                  ? 'w-6 h-2 bg-white'
                  : 'w-2 h-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdviceStoryModal;
